'use client';

import type {
  QueryClient,
  QueryKey,
  UseMutationOptions,
  UseQueryResult,
} from '@tanstack/react-query';
import {
  skipToken,
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

import { useAuth } from '@sola/auth-sdk/store.tsx';

import { MOCK_SUPPORTER, QUERY_KEYS } from '../constants.ts';

export const useReadyForQueries = () => {
  const { authUser } = useAuth('authUser');
  return {
    isReady: !!MOCK_SUPPORTER || !!authUser?.authUserGlobalId,
    globalId: authUser?.authUserGlobalId ?? MOCK_SUPPORTER?.globalId,
    contactId: authUser?.authUserPrimaryContactId,
  };
};

const useDataPrimer = (globalId: string | undefined) => {
  const queryKey = [QUERY_KEYS.dataPrimerReadOnly, globalId];
  const primerQuery = useQuery({
    queryKey,
    queryFn: skipToken,
    enabled: false,
  });

  return {
    isPrimerReady:
      !!MOCK_SUPPORTER ||
      (primerQuery.status !== 'pending' &&
        primerQuery.fetchStatus !== 'fetching'),
  };
};

type AnySupporterQuery<TData = unknown> = {
  queryKey?: QueryKey;
  queryFn: (
    globalId: string,
    queryContext?: {
      queryClient: QueryClient;
    },
  ) => Promise<TData>;
  waitForPrimer?: boolean;
};

type SupporterMutationOptions<TData, TError, TVariables, TContext> =
  UseMutationOptions<TData, TError, TVariables, TContext> & {
    mockMutationFn?: NonNullable<
      UseMutationOptions<TData, TError, TVariables, TContext>['mutationFn']
    >;
    mockOnSuccess?: NonNullable<
      UseMutationOptions<TData, TError, TVariables, TContext>['onSuccess']
    >;
  };

type InferSupporterQueryData<TQuery extends AnySupporterQuery> = Awaited<
  ReturnType<TQuery['queryFn']>
>;

type SupporterQueryTupleData<TQueries extends readonly AnySupporterQuery[]> = {
  [K in keyof TQueries]: TQueries[K] extends AnySupporterQuery
    ? InferSupporterQueryData<TQueries[K]> | undefined
    : never;
};

type SupporterQueryTupleResult<TQueries extends readonly AnySupporterQuery[]> =
  {
    data: SupporterQueryTupleData<TQueries>;
    isFetched: boolean;
    isFetching: boolean;
    isLoading: boolean;
    isPending: boolean;
    isPaused: boolean;
    isError: boolean;
    isSuccess: boolean;
    queries: ReturnType<typeof useQueries>;
  };

export function useSupporterQuery<TQuery extends AnySupporterQuery>(
  supporterQueries: TQuery,
): UseQueryResult<InferSupporterQueryData<TQuery>>;
export function useSupporterQuery<
  const TQueries extends readonly AnySupporterQuery[],
>(supporterQueries: TQueries): SupporterQueryTupleResult<TQueries>;
export function useSupporterQuery(
  supporterQueries: AnySupporterQuery | readonly AnySupporterQuery[],
):
  | UseQueryResult<unknown>
  | SupporterQueryTupleResult<readonly AnySupporterQuery[]> {
  const { isReady, globalId } = useReadyForQueries();
  const { isPrimerReady } = useDataPrimer(globalId);
  const queryClient = useQueryClient();
  const queries = Array.isArray(supporterQueries)
    ? supporterQueries
    : [supporterQueries];

  const queryResults = useQueries({
    queries: queries.map((supporterQuery) => {
      // TODO: Attempting to self-heal is causing infinite loops for payment-settings/giving-history
      // const existingQueryState = supporterQuery.queryKey
      //   ? queryClient.getQueryState(supporterQuery.queryKey)
      //   : undefined;
      // const isRemountAfterError = existingQueryState?.status === 'error';

      return {
        queryKey: [...(supporterQuery.queryKey ?? []), globalId],
        // We should always have a globalId if this runs bc of isReady logic.
        queryFn: async () => {
          if (MOCK_SUPPORTER) {
            await new Promise((resolve) => setTimeout(resolve, 1000));
          }

          return supporterQuery.queryFn(globalId as string, {
            queryClient,
          });
        },
        enabled: !!isReady && (!supporterQuery.waitForPrimer || isPrimerReady),
        refetchOnMount: false,
        refetchOnWindowFocus: false,
        retryOnMount: false,
        // retry: isRemountAfterError ? false : undefined,
        // retryOnMount: true,
      };
    }),
  });

  if (!Array.isArray(supporterQueries)) {
    return queryResults[0] as UseQueryResult<unknown>;
  }

  return {
    data: queryResults.map((result) => result.data),
    isFetched: queryResults.every((result) => result.isFetched),
    isFetching: queryResults.some((result) => result.isFetching),
    isLoading: queryResults.some((result) => result.isLoading),
    isPending: queryResults.some((result) => result.isPending),
    isPaused: queryResults.some((result) => result.isPaused),
    isError: queryResults.some((result) => result.isError),
    isSuccess: queryResults.every((result) => result.isSuccess),
    queries: queryResults,
  } as SupporterQueryTupleResult<readonly AnySupporterQuery[]>;
}

export const useSupporterMutation = <
  TData = unknown,
  TError = unknown,
  TVariables = void,
  TContext = unknown,
>(
  options: SupporterMutationOptions<TData, TError, TVariables, TContext>,
) => {
  const { mockMutationFn, mockOnSuccess, ...mutationOptions } = options;

  return useMutation<TData, TError, TVariables, TContext>({
    ...mutationOptions,
    ...(!!MOCK_SUPPORTER && {
      mutationFn: async (variables, context) => {
        await new Promise((resolve) => setTimeout(resolve, 1000));

        if (mockMutationFn) {
          return mockMutationFn(variables, context);
        }

        return true as TData;
      },
      onSuccess:
        mockOnSuccess ??
        (mockMutationFn ? mutationOptions.onSuccess : () => alert('Success!')),
    }),
  });
};
